import { ProductThumbnailImage } from "@/components/product/ProductThumbnailImage";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { handleActionError } from "@/lib/errors";
import {
  useAddAdHocSellerCartItem,
  useAdHocSellerSuggestion
} from "@/queries/cart";
import { formatPrice } from "@/utils/money";
import type { SalonStockerHttpTypes } from "@repo/api-sdk";
import { Select, Textarea, toast } from "@medusajs/ui";
import { memo, useEffect, useMemo, useState, type FormEvent } from "react";
import {
  canSubmitAdHocForm,
  parseAdHocCapture,
  parseAdHocPriceInput,
  parseAdHocQuantityInput
} from "./parseAdHocCapture";

type AdHocItemCapture = SalonStockerHttpTypes.AdHocItemCapture;

export interface AdHocPasteInDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * The ad hoc supplier item "paste-in" form (KAN-193), shown only to a
 * concierge operator (gated by the caller - see `AdHocPasteInButton`).
 *
 * Caroline captures a product from a supplier's Shopify storefront with the
 * browser extension, which writes an `AdHocItemCapture` to the clipboard. She
 * pastes that JSON here; it is parsed and validated (never trusted blindly -
 * see `parseAdHocCapture`), previewed, and she confirms/edits the price and
 * quantity before choosing a seller and submitting. The seller dropdown is
 * always present and always overridable - `suggested_seller_id` only
 * pre-selects it once, it never substitutes for her own choice (see
 * `StoreAddAdHocLineItem.seller_id`'s doc).
 *
 * Every field resets when the dialog re-opens (mirrors `SavedListNameDialog`),
 * so a previous add's leftovers never bleed into the next paste.
 */
export const AdHocPasteInDialog = memo(function AdHocPasteInDialog({
  open,
  onOpenChange
}: AdHocPasteInDialogProps) {
  const [rawJson, setRawJson] = useState("");
  const [parseError, setParseError] = useState<string | null>(null);
  const [capture, setCapture] = useState<AdHocItemCapture | null>(null);
  const [sellerId, setSellerId] = useState<string | null>(null);
  const [priceInput, setPriceInput] = useState("");
  const [quantityInput, setQuantityInput] = useState("1");
  // Whether the seller field has been settled once already (by a suggestion
  // landing or Caroline picking one herself) - guards the pre-select effect
  // below from re-firing and clobbering a deliberate choice.
  const [sellerSettled, setSellerSettled] = useState(false);

  const addAdHocItem = useAddAdHocSellerCartItem();

  const suggestion = useAdHocSellerSuggestion(capture?.source_hosts, {
    enabled: Boolean(capture)
  });
  const sellers = suggestion.data?.sellers ?? [];

  // A fresh slate every time the dialog opens - a stale paste, price edit or
  // seller choice from the last add must never linger into the next one.
  useEffect(() => {
    if (!open) return;
    setRawJson("");
    setParseError(null);
    setCapture(null);
    setSellerId(null);
    setPriceInput("");
    setQuantityInput("1");
    setSellerSettled(false);
  }, [open]);

  // Pre-selects the suggested seller the first time a suggestion for the
  // current capture arrives. Only runs while nothing has been settled yet, so
  // it can never overwrite a choice Caroline already made (including
  // "deliberately still empty" after she opened and closed the dropdown).
  useEffect(() => {
    if (sellerSettled) return;
    const suggestedId = suggestion.data?.suggested_seller_id;
    if (!suggestedId) return;
    setSellerId(suggestedId);
    setSellerSettled(true);
  }, [suggestion.data?.suggested_seller_id, sellerSettled]);

  const handleJsonChange = (value: string) => {
    setRawJson(value);

    if (!value.trim()) {
      setParseError(null);
      setCapture(null);
      return;
    }

    const result = parseAdHocCapture(value);
    if (!result.ok) {
      setParseError(result.error);
      setCapture(null);
      return;
    }

    setParseError(null);
    setCapture(result.capture);
    // A new capture starts its own seller decision - the previous paste's
    // suggestion (or choice) says nothing about this one's supplier.
    setSellerId(null);
    setSellerSettled(false);
    setPriceInput(
      result.capture.price != null ? String(result.capture.price) : ""
    );
    setQuantityInput("1");
  };

  const unitPrice = useMemo(
    () => parseAdHocPriceInput(priceInput),
    [priceInput]
  );
  const quantity = useMemo(
    () => parseAdHocQuantityInput(quantityInput),
    [quantityInput]
  );

  const canSubmit =
    Boolean(capture) &&
    canSubmitAdHocForm({ sellerId, unitPrice, quantity }) &&
    !addAdHocItem.isPending;

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!capture || !sellerId || unitPrice === null || quantity === null) {
      return;
    }

    try {
      await addAdHocItem.mutateAsync({
        seller_id: sellerId,
        shopify_variant_id: capture.shopify_variant_id,
        shopify_product_id: capture.shopify_product_id,
        title: capture.title,
        image_url: capture.image_url,
        unit_price: unitPrice,
        quantity,
        variant_title: capture.variant_title
      });
      toast.success("Added to cart", {
        description: `${capture.title} was added at ${formatPrice(unitPrice)} each.`
      });
      onOpenChange(false);
    } catch (error) {
      toast.error("Couldn't add this item", {
        description: handleActionError(error, {
          boundary: "ad hoc paste-in dialog",
          fallback: "Could not add this item. Please try again."
        })
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Add a supplier item</DialogTitle>
            <DialogDescription>
              Paste the product you copied from the supplier&apos;s site.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label
                htmlFor="ad-hoc-json"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                Captured product
              </label>
              <Textarea
                id="ad-hoc-json"
                value={rawJson}
                onChange={(event) => handleJsonChange(event.target.value)}
                placeholder="Paste the copied product here"
                rows={4}
                autoFocus
              />
              {parseError && (
                <p
                  role="alert"
                  className="mt-2 text-sm text-red-600 dark:text-red-400"
                >
                  {parseError}
                </p>
              )}
            </div>

            {capture && (
              <>
                {/* Confirmation preview - lets Caroline check she captured the
                    product she meant to before she commits price/quantity. */}
                <div className="flex items-center gap-3 rounded-md border border-gray-200 bg-gray-50 p-3">
                  <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-md bg-gray-100">
                    <ProductThumbnailImage
                      src={capture.image_url ?? null}
                      alt={capture.title}
                      className="h-full w-full object-cover"
                      enablePreview={false}
                      fallback={
                        <div className="flex h-full w-full items-center justify-center text-gray-400">
                          <span className="text-xs">No image</span>
                        </div>
                      }
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-gray-900">
                      {capture.title}
                    </p>
                    {capture.variant_title && (
                      <p className="truncate text-xs text-gray-500">
                        {capture.variant_title}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor="ad-hoc-price"
                      className="mb-1 block text-sm font-medium text-gray-700"
                    >
                      Price
                    </label>
                    <Input
                      id="ad-hoc-price"
                      inputMode="decimal"
                      value={priceInput}
                      onChange={(event) => setPriceInput(event.target.value)}
                      placeholder="0.00"
                      aria-label="Price"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="ad-hoc-quantity"
                      className="mb-1 block text-sm font-medium text-gray-700"
                    >
                      Quantity
                    </label>
                    <Input
                      id="ad-hoc-quantity"
                      inputMode="numeric"
                      value={quantityInput}
                      onChange={(event) => setQuantityInput(event.target.value)}
                      aria-label="Quantity"
                    />
                  </div>
                </div>

                <div>
                  <label
                    id="ad-hoc-seller-label"
                    className="mb-1 block text-sm font-medium text-gray-700"
                  >
                    Seller
                  </label>
                  <Select
                    value={sellerId ?? ""}
                    onValueChange={(value) => {
                      // Radix's Select mirrors a hidden native <select> for
                      // form semantics and re-syncs it whenever the
                      // controlled `value` changes underneath it (e.g. the
                      // suggestion effect below setting a pre-selection) -
                      // that resync can itself dispatch a stray
                      // `onValueChange("")` before the native mirror's
                      // options have caught up. No real seller ever has an
                      // empty id, so treat that call as a no-op rather than
                      // clobbering a value nothing here actually cleared.
                      if (!value) return;
                      setSellerId(value);
                      setSellerSettled(true);
                    }}
                    disabled={suggestion.isLoading}
                  >
                    <Select.Trigger aria-labelledby="ad-hoc-seller-label">
                      <Select.Value placeholder="Select a seller" />
                    </Select.Trigger>
                    <Select.Content>
                      {sellers.map((seller) => (
                        <Select.Item key={seller.id} value={seller.id}>
                          {seller.name}
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select>
                </div>
              </>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={addAdHocItem.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {addAdHocItem.isPending ? "Adding…" : "Add to cart"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
});
